import { DID_CREATE_PAYMENT, DID_FAIL_CREATING_PAYMENT, WILL_CREATE_PAYMENT } from '../constants/index';

import fetch from '../utils/fetch-json';
import { getFormActions } from '../utils/form-helpers';
import formConfig from './stripe-form-config';
import {
    getAnalyticsTrackingInfoForPayment,
    trackEvent,
    trackPixelEvent,
} from '../support-and-tracking/analytics-actions';
import { produceGlobalError } from '../utils/error';
import { CART_USER_TOKEN_IDENTIFIER } from '../cart/cart-actions';
import storage from '../utils/storage';

const { setErrors, setFormField } = getFormActions(formConfig);
const SessionStorage = storage('sessionStorage');

export const initiateStripePayment =
    ({
        projectId,
        amount,
        confirmationTokenId,
        isFinished,
        title,
        email,
        rewardGiverInfoMetadata,
        stripe,
        giverName,
        commentImageUrl,
        comment,
        memorialCommentId,
        rewards,
        subprojectId,
        projectPollOptionId,
        international,
        consentedToPersonalizedNewsletter,
    }) =>
    async ({ dispatch, getState }) => {
        dispatch(setFormField('isProcessingPayment', true));
        dispatch({ type: WILL_CREATE_PAYMENT });
        const state = getState();
        const stripeGiveApiUrl = state.baseURL.concat(`/api/public/stripe/${projectId}/give`);
        const stripeRequiresActionApiUrl = state.baseURL.concat(`/api/public/stripe/${projectId}/action-completed`);
        const analyticsTrackingInfo = await getAnalyticsTrackingInfoForPayment({ state, projectId });

        try {
            const response = await fetch({
                url: stripeGiveApiUrl,
                options: {
                    method: 'POST',
                    body: {
                        projectId,
                        title,
                        amount,
                        rewards,
                        confirmationTokenId,
                        rewardGiverInfoMetadata,
                        email,
                        nameAndComment: {
                            giverName,
                            comment,
                            commentImageUrl,
                            anonymous: giverName ? false : undefined,
                        },
                        subprojectId,
                        projectPollOptionId,
                        memorialCommentId,
                        userTokenId: SessionStorage.get(CART_USER_TOKEN_IDENTIFIER),
                        analyticsTrackingInfo,
                        international,
                        consentedToPersonalizedNewsletter,
                    },
                },
                dispatch,
                getState,
            });

            return await dispatch(
                handleServerResponse({
                    stripeRequiresActionApiUrl,
                    stripe,
                    response,
                    isFinished,
                    projectId,
                    memorialCommentId,
                })
            );
        } catch (error) {
            handleError(error.body, dispatch, isFinished);
        }
    };

const handleServerResponse =
    ({ stripeRequiresActionApiUrl, stripe, response, isFinished, projectId, memorialCommentId }) =>
    async ({ dispatch, getState }) => {
        if (response.error) {
            dispatch(setFormField('requiresAction', false));
            return handleError(
                produceGlobalError('Kunne ikke gjennomføre betalingen, prøv igjen senere.'),
                dispatch,
                isFinished
            );
        } else if (response.body?.requires_action) {
            dispatch(setFormField('requiresAction', true));
            let result;
            try {
                result = await stripe.handleNextAction({ clientSecret: response.body.payment_intent_client_secret });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn(
                    `Stripe handle next action failed: ${error.body}. Trying to check if the payment intent ${response.body.payment_intent_id} succeeded.`
                );
                return confirmResult({
                    stripeRequiresActionApiUrl,
                    stripe,
                    paymentIntentId: response.body.payment_intent_id,
                    dispatch,
                    getState,
                    isFinished,
                    stripeTransactionId: response.body.stripe_transaction_id,
                    projectId,
                    memorialCommentId,
                });
            }
            if (result.error) {
                dispatch(setFormField('requiresAction', false));
                return handleError(
                    produceGlobalError(
                        'Vi klarer ikke å se om du autentiserte betalingen eller ikke. Vi har ikke trukket penger fra kortet ditt!'
                    ),
                    dispatch,
                    isFinished
                );
            } else {
                return confirmResult({
                    stripeRequiresActionApiUrl,
                    stripe,
                    paymentIntentId: result.paymentIntent.id,
                    dispatch,
                    getState,
                    isFinished,
                    stripeTransactionId: response.body.stripe_transaction_id,
                    projectId,
                    memorialCommentId,
                });
            }
        } else {
            dispatch(setFormField('requiresAction', false));
            dispatch({ type: DID_CREATE_PAYMENT, data: response.body });
            isFinished({
                didGiveSupport: true,
                transactionForGiverInfo: getState().transactions.transactionForGiverInfo,
            });

            trackPixelEvent('Purchase', { value: response.body.transaction.amount, currency: 'NOK' });
            trackEvent('support', 'checkout-complete', {
                method: 'Stripe',
                'project-id': response.body.project.id,
                amount: response.body.transaction.amount,
            });
        }
    };

const confirmResult = async ({
    stripe,
    stripeRequiresActionApiUrl,
    paymentIntentId,
    isFinished,
    dispatch,
    getState,
    stripeTransactionId,
    projectId,
    memorialCommentId,
}) => {
    try {
        const confirmResult = await fetch({
            url: stripeRequiresActionApiUrl,
            options: {
                method: 'POST',
                body: { paymentIntentId: paymentIntentId, stripeTransactionId: stripeTransactionId },
            },
            dispatch,
            getState,
        });
        return dispatch(
            handleServerResponse({
                stripeRequiresActionApiUrl,
                stripe,
                response: confirmResult,
                isFinished,
                projectId,
                memorialCommentId,
            })
        );
    } catch (error) {
        dispatch(setFormField('requiresAction', false));
        return handleError(error.body, dispatch, isFinished);
    }
};

const handleError = (errorBody, dispatch, isFinished) => {
    dispatch(setErrors(errorBody?.errors));
    stopLoading(dispatch);
    isFinished();
    return dispatch({ type: DID_FAIL_CREATING_PAYMENT, errors: errorBody?.errors });
};

function stopLoading(dispatch) {
    dispatch(setFormField('isProcessingPayment', false));
}
